import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Cliente, ModuloDisponible, Plan } from "src/app/shared/model/security/plan.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { PlataformaService } from "../../plataforma.service";

/** Lo que la lista le pasa al modal: el cliente siempre; plan null = contratar un módulo nuevo. */
export interface DatosModalPlan {
  cliente: Cliente;
  plan: Plan | null;
}

/**
 * Alta o ajuste de un plan de UN cliente, desde la plataforma.
 *
 * DOS COSAS QUE ESTE MODAL TIENE QUE DEJAR CLARAS
 *
 * 1. El módulo se elige una vez. En el ajuste no se cambia: cambiarlo sería
 *    mover las licencias de una cosa a otra, y los perfiles ya repartidos
 *    quedarían colgando del módulo equivocado. Para otro módulo se contrata
 *    otro plan.
 * 2. El cupo no puede bajar de lo ya ocupado. No expulsaría a nadie, pero
 *    dejaría al cliente sin poder dar de alta y sin ninguna pista de por qué;
 *    el servidor lo rechaza y aquí se avisa antes de intentarlo.
 */
@Component({
  selector: "app-modal-plan",
  standalone: true,
  templateUrl: "./modal-plan.component.html",
  styleUrls: ["./modal-plan.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ModalPlanComponent implements OnInit {
  plan = new Plan();
  modulos: ModuloDisponible[] = [];
  cargandoModulos = false;
  guardando = false;

  constructor(
    private dialogRef: MatDialogRef<ModalPlanComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DatosModalPlan,
    private plataformaService: PlataformaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.data?.plan) {
      this.plan = { ...this.data.plan, vigencia: this.soloFecha(this.data.plan.vigencia) };
    } else {
      this.plan = new Plan();
      this.plan.vigencia = this.dentroDeUnAnio();
      this.cargarModulos();
    }
    // El corporativo va siempre explícito: es el del cliente que se está
    // administrando, nunca el de quien usa la plataforma.
    this.plan.idCliente = this.data.cliente.idCorporativo;
  }

  get esAlta(): boolean {
    return !this.plan.id;
  }

  get titulo(): string {
    return this.esAlta ? `Contratar módulo para ${this.data.cliente.nombre}` : `${this.plan.sitio} · ${this.data.cliente.nombre}`;
  }

  get minimoCupo(): number {
    return Math.max(1, this.plan.usuariosAsignados ?? 0);
  }

  get cupoInsuficiente(): boolean {
    return (this.plan.cantidadUsuarios ?? 0) < this.minimoCupo;
  }

  get puedeGuardar(): boolean {
    return (
      !this.guardando &&
      !!this.plan.vigencia &&
      (this.plan.cantidadUsuarios ?? 0) >= 1 &&
      !this.cupoInsuficiente &&
      (!this.esAlta || !!this.plan.sitio)
    );
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }

  guardar(): void {
    if (!this.puedeGuardar) {
      return;
    }
    this.guardando = true;
    this.cdr.detectChanges();

    this.plataformaService.guardarPlan(this.plan).subscribe({
      next: (respuesta) => {
        this.guardando = false;
        if (respuesta?.pCodRetorno && respuesta.pCodRetorno !== "00000") {
          this.msg.warningCall(respuesta.pMensaje, null, null);
          this.cdr.detectChanges();
          return;
        }
        this.msg.successCall(
          this.esAlta
            ? `${this.plan.sitio} quedó contratado para ${this.data.cliente.nombre}.`
            : "El plan quedó actualizado.",
          null,
          null
        );
        this.dialogRef.close(true);
      },
      error: () => {
        this.guardando = false;
        this.msg.errorCall("No se pudo guardar el plan.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  // ------------------------------------------------------------------

  private cargarModulos(): void {
    this.cargandoModulos = true;
    this.plataformaService.consultarModulosDisponibles(this.data.cliente.idCorporativo).subscribe({
      next: (respuesta) => {
        this.modulos = respuesta?.list ?? [];
        this.cargandoModulos = false;
        if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
          this.msg.warningCall(respuesta.pMensaje, null, null);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.modulos = [];
        this.cargandoModulos = false;
        this.msg.errorCall("No se pudieron consultar los módulos disponibles.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  private soloFecha(valor: string | null): string | null {
    if (!valor) {
      return null;
    }
    return valor.length >= 10 ? valor.substring(0, 10) : valor;
  }

  /** Un año es el plazo habitual; se puede cambiar antes de guardar. */
  private dentroDeUnAnio(): string {
    const fecha = new Date();
    fecha.setFullYear(fecha.getFullYear() + 1);
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }
}
